// PPCMainContent.jsx
import "./PPCMainContent.scss";
import "./PPCMainContentMobile.scss";
import React, { useState, useEffect } from "react";
import sale from "../../../Assets/Design/Sale.png";
import ProductText from "./ProductText";
import MainContentModal from "./MainContentModal/MainContentModal";
import ViewProductModal from "./MainContentModal/ViewProductModal/ViewProductModal";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../Components/AppProvider";
import ViewStarReview from "./viewStarReview";
import { useMediaQuery } from "react-responsive";

function PPCMainContent({
  filterData,
  setFilterData,
  sortVal,
  restoredPageData,
  searchTerm,
}) {
  const [productData, setProductData] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [likedMap, setLikedMap] = useState({});
  const [noMatch, setNoMatch] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [productId, setProductId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const [ratingsMap, setRatingsMap] = useState({});
  const [wishToggle, setWishToggle] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 600 });

  const navigate = useNavigate();
  const { setHasBagCount, setCustomerId, setHasWishlistCount } =
    useAppContext(); // ✅ grab bag count

  // ----------------- Add to Bag -----------------
  const HandleAddToBag = async (productId, quantity) => {
    setIsAdding(true);
    try {
      // Check auth first
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/customer-profile`,
        {
          credentials: "include",
        },
      );
      const data = await res.json();

      if (!data?.CP_ID) {
        const confirmLogin = window.confirm(
          "You need to be logged in to add this product to your bag. Do you want to log in now?",
        );

        if (!confirmLogin) return;

        localStorage.setItem(
          "redirectAfterLogin",
          JSON.stringify({
            path: window.location.pathname,
            scrollTop: window.scrollY,
            productId,
            quantity,
            filterData,
          }),
        );

        navigate("/login");
        return;
      }

      setCustomerId(data.CP_ID);
      const addRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/bag/add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            CP_ID: data.CP_ID,
            PD_ID: productId,
            B_Quantity: quantity,
          }),
        },
      );

      const result = await addRes.json();
      if (addRes.ok) {
        setHasBagCount(true);
        alert(result.message);
        localStorage.removeItem("redirectAfterLogin");
        window.dispatchEvent(new Event("bagUpdated"));
        setModalOpen(false);
      } else {
        alert(result.error || "Failed to add to bag.");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    }
    setIsAdding(false);
  };
  const redirectState = JSON.parse(localStorage.getItem("redirectAfterLogin"));
  // ----------------- Restore modal after login -----------------
  useEffect(() => {
    console.log("redirectState?.productId", redirectState?.productId);
    if (redirectState?.productId) {
      setProductId(redirectState.productId);
      setQuantity(redirectState.quantity || 1);
      setModalOpen(true);
    }
  }, []);

  // ----------------- Fetch products -----------------
  useEffect(() => {
    const fetchProductsAndRatings = async () => {
      setLoading(true);

      try {
        // ✅ Get logged in user first
        const profileRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/auth/customer-profile`,
          { credentials: "include" },
        );
        const profile = await profileRes.json();
        const CP_ID = profile?.CP_ID || null;

        // ✅ Fetch product & ratings in parallel
        const [productRes, ratingRes] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_BASE_URL}api/product/get-all-products?CP_ID=${CP_ID}`,
          ),
          fetch(
            `${process.env.REACT_APP_BASE_URL}api/rate/get-all-average-ratings`,
          ),
        ]);

        const productData = await productRes.json();
        const ratingData = await ratingRes.json();

        setProductData(productData.product || []);

        // ⭐ Build the ratings map
        const ratingMap = (ratingData.ratings || []).reduce((acc, r) => {
          acc[r.PD_ID] = {
            averageRating: parseFloat(r.averageRating) || 0,
            totalReviews: r.totalReviews || 0,
          };
          return acc;
        }, {});
        setRatingsMap(ratingMap);

        // ✅ Build likedMap based on backend `isLiked`
        const likedInit = {};
        (productData.product || []).forEach((p) => {
          likedInit[p.PD_ID] = p.isLiked === 1;
        });
        setLikedMap(likedInit);
      } catch (err) {
        console.error("Failed to fetch products or ratings", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndRatings();
  }, []);

  // ----------------- Filtering & Sorting -----------------
  const isFilterActive = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value !== "all" && value !== "";
    if (typeof value === "number") return value !== 0 && !isNaN(value);
    return Boolean(value);
  };

  const sortFilter = (products) => {
    if (!products.length || !sortVal) return products;

    return [...products].sort((a, b) => {
      const getPrice = (p) =>
        p.PD_ProductType === "onhand" ? p.PD_SalePrice : p.PD_OriginalPrice;

      // ⭐ Get total reviews (used for "most popular" sort)
      const getPopularity = (p) => ratingsMap[p.PD_ID]?.totalReviews || 0;

      switch (sortVal) {
        case "newest":
          return new Date(b.PD_DateUploaded) - new Date(a.PD_DateUploaded);
        case "oldest":
          return new Date(a.PD_DateUploaded) - new Date(b.PD_DateUploaded);
        case "lowest-price":
          return getPrice(a) - getPrice(b);
        case "highest-price":
          return getPrice(b) - getPrice(a);
        case "alphabetical-asc":
          return a.PD_Name.localeCompare(b.PD_Name);
        case "alphabetical-desc":
          return b.PD_Name.localeCompare(a.PD_Name);
        case "most-popular":
          // Sort descending by total reviews (most popular first)
          return getPopularity(b) - getPopularity(a);
        default:
          return 0;
      }
    });
  };

  useEffect(() => {
    if (!productData.length) return;

    const hasFilters = Object.values(filterData).some(isFilterActive);
    let filtered = productData;

    if (hasFilters) {
      filtered = productData.filter((p) => {
        const productDate = p.PD_Preorderdeadline
          ? new Date(p.PD_Preorderdeadline)
          : new Date(p.PD_DateUploaded);
        const productMonth = productDate.getMonth();
        const productYear = productDate.getFullYear();

        const matchOrderType =
          !isFilterActive(filterData.orderType) ||
          p.PD_ProductType.toLowerCase() === filterData.orderType.toLowerCase();

        const matchCategory =
          !isFilterActive(filterData.categories) ||
          filterData.categories.includes(Number(p.PC_ID));
        const matchAnime =
          !isFilterActive(filterData.animeSeries) ||
          filterData.animeSeries.includes(Number(p.AS_ID));
        const matchManufacturer =
          !isFilterActive(filterData.manufacturers) ||
          filterData.manufacturers.includes(Number(p.PM_ID));
        const matchMonth =
          !filterData.month || filterData.month === "all"
            ? true
            : Number(filterData.month) === productMonth;
        const matchYear =
          !filterData.year || filterData.year === "all"
            ? true
            : Number(filterData.year) === productYear;

        const actualPrice =
          p.PD_ProductType === "onhand" ? p.PD_SalePrice : p.PD_OriginalPrice;
        const matchPrice =
          (!filterData.minPrice || actualPrice >= filterData.minPrice) &&
          (!filterData.maxPrice || actualPrice <= filterData.maxPrice);

        const productRating = ratingsMap[p.PD_ID]?.averageRating || 0;
        const matchStar =
          !isFilterActive(filterData.star) || productRating >= filterData.star;

        return (
          matchOrderType &&
          matchCategory &&
          matchAnime &&
          matchManufacturer &&
          matchMonth &&
          matchYear &&
          matchPrice &&
          matchStar
        );
      });
    }

    // ✅ Apply search filtering
    if (searchTerm && searchTerm.trim() !== "") {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.PD_Name.toLowerCase().includes(lowerSearch) ||
          (p.PD_Description &&
            p.PD_Description.toLowerCase().includes(lowerSearch)),
      );
    }

    setDisplayedProducts(sortFilter(filtered));
    setNoMatch(filtered.length === 0);
  }, [filterData, productData, sortVal, searchTerm, wishToggle]);

  const toggleLike = async (productId) => {
    try {
      // Check login
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/auth/customer-profile`,
        {
          credentials: "include",
        },
      );
      const data = await res.json();

      if (!data?.CP_ID) {
        const confirmLogin = window.confirm(
          "You need to log in to add this product to your wishlist. Log in now?",
        );
        if (confirmLogin) navigate("/login");
        return;
      }

      const customerId = data.CP_ID;
      const isLiked = likedMap[productId];

      // Toggle add/remove
      const command = isLiked ? "remove" : "add";
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/wishlist/add-drop-wish/${productId}/${customerId}/${command}`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      const result = await response.json();

      if (response.ok) {
        setLikedMap((prev) => ({ ...prev, [productId]: !isLiked }));
        setHasWishlistCount(true);
        //console.log(result.message);
      } else {
        alert(result.error || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
    }
  };

  // ✅ FIX: Corrected loading condition
  if (loading)
    return (
      <div className="loading-spinner">
        <i className="fa fa-spinner fa-spin" />
        <p>Loading...</p>
      </div>
    );
  const isProductAvailable = (
    PD_ProductType,
    PD_Stock,
    PD_Preorderdeadline,
  ) => {
    if (PD_ProductType === "onhand") {
      return PD_Stock > 0 ? true : false;
    } else if (PD_ProductType === "preorder") {
      return new Date(PD_Preorderdeadline) >= new Date() ? true : false;
    }
  };
  return (
    <>
      <div className="PPCMC-container scrollable-div">
        {displayedProducts.length ? (
          displayedProducts.map((product) => (
            <div className="PPCMC-card-wrapper" key={product.PD_ID}>
              <div className="prod-picture-wrapper">
                <button
                  className={`prod-wishlist-icon ${
                    likedMap[product.PD_ID] ? "is-active" : ""
                  }`}
                  onClick={() => toggleLike(product.PD_ID)}
                >
                  <svg width="18" height="16" viewBox="0 0 18 16">
                    <path
                      d="M9.01163699,14.9053769 C8.72930024,14.7740736 8.41492611,14.6176996 8.07646224,14.4366167 C7.06926649,13.897753 6.06198912,13.2561336 5.12636931,12.5170512 C2.52930452,10.4655288 1.00308384,8.09476443 1.00000218,5.44184117 C0.997549066,2.99198843 2.92175104,1.01242822 5.28303025,1.01000225 C6.41066623,1.00972036 7.49184369,1.4629765 8.28270844,2.2678673 L8.99827421,2.9961237 L9.71152148,2.26559643 C10.4995294,1.45849728 11.5791258,1.0023831 12.7071151,1.00000055 L12.7060299,1.00000225 C15.0693815,0.997574983 16.9967334,2.97018759 17.0000037,5.421337 C17.0038592,8.07662382 15.4809572,10.4530151 12.8850542,12.5121483 C11.9520963,13.2521931 10.9477036,13.8951276 9.94340074,14.4354976 C9.60619585,14.6169323 9.29297309,14.7736855 9.01163699,14.9053769 Z"
                      strokeWidth="1"
                    />
                  </svg>
                </button>

                {/* SALE Badge (only show if there's a sale price) */}
                {product.PD_SalePrice !== 0 && product.PD_Stock > 0 && (
                  <img className="prod-sale-badge" src={sale} />
                )}

                <img
                  src={`${process.env.REACT_APP_BASE_URL}Assets/Products/${product.PD_ID}/${product.PD_ProductPicPath[0]}`}
                  alt={product.PD_Name}
                  onClick={() => {
                    if (!isMobile) {
                      setProductId(product.PD_ID);
                      setQuantity(1);
                      setModalOpen(true);
                    }
                  }}
                />

                <div
                  className={`prod-type-wrapper ${
                    isProductAvailable(
                      product.PD_ProductType,
                      product.PD_Stock,
                      product.PD_Preorderdeadline,
                    ) === false
                      ? "notAvailable-wrapper"
                      : ""
                  }`}
                >
                  <span>
                    {product.PD_ProductType === "onhand"
                      ? product.PD_Stock > 0
                        ? `IN STOCK (${product.PD_Stock})`
                        : "OUT OF STOCK"
                      : new Date(product.PD_Preorderdeadline) >= new Date()
                        ? "PRE ORDER"
                        : "PRE ORDER ENDED"}
                  </span>
                </div>
              </div>

              <div className="prod-card-details">
                <ProductText text={product.PD_Name} />

                {product.PD_ProductType === "onhand" &&
                product.PD_SalePrice !== 0 &&
                product.PD_Stock > 0 ? (
                  <>
                    <span className="original-price">
                      {`PHP ${product.PD_OriginalPrice}`}
                    </span>
                    <span className="sale-price">
                      {` PHP ${product.PD_SalePrice}`}
                    </span>
                  </>
                ) : (
                  <span className="sale-price">
                    {`PHP ${product.PD_OriginalPrice}`}
                  </span>
                )}
              </div>

              <div className="prod-card-star">
                <ViewStarReview
                  PD_ID={product.PD_ID}
                  reviewValue={ratingsMap[product.PD_ID]?.averageRating || 0}
                />
                <span>
                  {ratingsMap[product.PD_ID]?.averageRating || 0} Star Rating |{" "}
                  {ratingsMap[product.PD_ID]?.totalReviews || 0} Sold
                </span>
              </div>
              <button
                onClick={() => {
                  navigate(`/products/${product.PD_ID}`, {
                    state: { isLiked: likedMap[product.PD_ID] },
                  });
                }}
              >
                VIEW FULL DETAILS
              </button>
            </div>
          ))
        ) : (
          <div className="no-products">
            No products match the selected filters.
          </div>
        )}
      </div>

      <MainContentModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
      >
        <ViewProductModal
          productId={productId}
          HandleAddToBag={HandleAddToBag}
          isAdding={isAdding}
          wishToggle={wishToggle}
          setWishToggle={setWishToggle}
          onToggleLike={toggleLike}
        />
      </MainContentModal>
    </>
  );
}

export default PPCMainContent;
